import React from "react"
import styled from "styled-components"
import { Socials } from "@bw/bits"
import { motion } from "framer-motion"
import { useTranslation } from "react-i18next"
import useSocialNetworks from "hooks/useSocialNetworks"

const Layer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  justify-content: flex-end;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9;
`

const InnerLayer = styled(motion.div)`
  color: var(--layer-text-color, var(--text-color));
  flex-direction: column;
  background-color: var(--layer-background-color, var(--background-color));
  margin-left: auto;
  padding: 0 20px;
  width: 100%;
  max-width: 400px;
`

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  padding: 18px 23px;
  margin-bottom: 40px;
`

const Body = styled.div`
  --scrollbar-width: 15px;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
`

const Footer = styled.footer`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto 0 0;
  padding: 20px 0 60px;
  width: 100%;
  border-top: 1px solid var(--layer-border-color, lightgray);
`

const variants = {
  open: { opacity: 1, display: "flex" },
  closed: { opacity: 0, transitionEnd: { display: "none" } },
}

const innerVariants = {
  open: { opacity: 1, x: 0, display: "flex" },
  closed: { opacity: 0, x: "40px", transitionEnd: { display: "none" } },
}

const LayerMenu = ({ children, show, setShow }) => {
  const { t } = useTranslation()
  const layerRef = React.useRef(null)
  const networks = useSocialNetworks()

  const closeOnOutsideClick = React.useCallback(
    (e, ref) => {
      if (e.target === ref.current) {
        setShow(false)
      }
    },
    [setShow]
  )

  React.useEffect(() => {
    const handleEsc = event => {
      if (event.keyCode === 27) {
        setShow(false)
      }
    }
    window.addEventListener("keydown", handleEsc)

    return () => {
      window.removeEventListener("keydown", handleEsc)
    }
  }, [setShow])

  return (
    <Layer
      animate={show ? "open" : "closed"}
      variants={variants}
      transition={{ duration: 0.4 }}
      initial={false}
      ref={layerRef}
      onClick={e => closeOnOutsideClick(e, layerRef)}
    >
      <InnerLayer
        animate={show ? "open" : "closed"}
        variants={innerVariants}
        transition={{ duration: 0.6 }}
        initial={false}
      >
        <Header>
          <div
            role="button"
            tabIndex="0"
            style={{
              display: "flex",
              alignItems: "center",
              fontWeight: "400",
              gap: "8px",
            }}
            onKeyDown={() => {
              setShow(false)
            }}
            onClick={() => {
              setShow(false)
            }}
          >
            {t("menu.close")}
            <svg aria-label="Close" viewBox="0 0 24 24" height="18" width="18">
              <path
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                d="m3 3 18 18M3 21 21 3"
              />
            </svg>
          </div>
        </Header>
        <Body>
          <Content>{children}</Content>
          {networks?.length > 0 && (
            <Footer>
              <Socials networks={networks} />
            </Footer>
          )}
        </Body>
      </InnerLayer>
    </Layer>
  )
}

export default LayerMenu
